import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useFeatureFlagsContext } from "@/contexts/FeatureFlagsContext";
import { useTheme } from "@/contexts/ThemeContext";
import DashboardSidebar from "../components/DashboardSidebar";
import DashboardGrid from "../components/DashboardGrid";
import { Loader2 } from "lucide-react";
import { apiClient, Report } from "@rti/shared";
import { ACCESS_LEVELS } from "@rti/shared";
import { trackUserEvent } from "@/utils/userTracking";

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user, isTrialExpired } = useAuth();
  const { getFlag, isLoading: isFeatureFlagsLoading } = useFeatureFlagsContext();
  const { isDark } = useTheme();
  const pageStyle: React.CSSProperties = {
    background: isDark
      ? '#0d0f0c'
      : 'radial-gradient(70% 60% at 50% 38%, #ffffff 0%, #e6f1df 100%)',
  };
  const [loading, setLoading] = useState(true);
  const [activeReports, setActiveReports] = useState<Report[]>([]);
  const [reportsGroupings, setReportsGroupings] = useState<{
    group1: Report[];
    group2?: Report[];
  }>({ group1: [] });
  const fetchInProgressRef = useRef(false);

  const gradients = ["green", "red", "purple", "plum"];
  const textColors = ["green", "red", "light-purple", "salmon"];

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/authorization");
    }
  }, [isLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isTrialExpired && user?.email) {
      trackUserEvent("trial_expired", "User Trial Expired", {
        user_id: user.user_id,
        first_name: user.first_name,
        last_name: user.last_name,
      });
    }
  }, [isTrialExpired, user?.email, user?.user_id, user?.first_name, user?.last_name]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (fetchInProgressRef.current || !user) return;

      fetchInProgressRef.current = true;

      try {
        setLoading(true);
        const reportsData: Report[] = await apiClient.getUserAccessibleReports(user.user_id);

        sortContainer(reportsData);
        setActiveReports(reportsData);

        const groupings = groupReports(reportsData);
        setReportsGroupings(groupings);
        setLoading(false);
      } catch (error) {
        const status = (error as any).response?.status;
        if (status === 403 || status === 404) {
          navigate("/authorization");
          return;
        }
        console.error("Error fetching dashboard data:", error);
        setLoading(false);
      } finally {
        fetchInProgressRef.current = false;
      }
    };

    if (isAuthenticated && user) {
      fetchDashboardData();
    }
  }, [isAuthenticated, user?.user_id, navigate]);



  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const sortContainer = (container: Report[]) => {
    if (!container) return;
    container.sort((a, b) => {
      const aSort = a.sort_order ?? 999;
      const bSort = b.sort_order ?? 999;
      if (aSort !== bSort) return aSort - bSort;
      return a.name.localeCompare(b.name);
    });
  };

  const groupReports = (activeReports: Report[]) => {
    const groupings: { group1: Report[]; group2?: Report[] } = { group1: [] };

    activeReports = getActiveReports(activeReports.filter(r => !r.name.includes("Petchem")));

    if (activeReports.length < 5) {
      groupings.group1 = activeReports;
      groupings.group2 = [];
    } else {
      groupings.group1 = activeReports.slice(0, 2);
      groupings.group2 = activeReports.slice(2);
    }
    return groupings;
  };

  const getActiveReports = (container: Report[] = []) => {
    return container
      .filter((r) => r.is_active && !!r.has_dashboard && !!r.abbreviation)
      .sort((a, b) => {
        const aSort = a.sort_order ?? 999;
        const bSort = b.sort_order ?? 999;
        if (aSort !== bSort) return aSort - bSort;
        return a.name.localeCompare(b.name);
      });
  };

  const hasEmployeeAccess = !!user && (user.effective_membership_id ?? 0) >= ACCESS_LEVELS.EMPLOYEE;
  const hasPremiumAccess = !!user && (user.effective_membership_id ?? 0) >= ACCESS_LEVELS.COMPOUND;
  const useResinDashboards = !isFeatureFlagsLoading && getFlag("marketintel_resin_dashboards");


  return (
    <div className="flex items-start justify-center w-full min-h-screen" style={pageStyle}>
      {isLoading || loading ? (
        <div className="flex items-center justify-center min-h-screen">
          <div className="flex items-center justify-center h-full w-full">
            <Loader2 className="animate-spin opacity-30 w-16 h-16" />
          </div>
        </div>
      ) : !isAuthenticated ? null : isTrialExpired ? (
        <div className="flex flex-col items-center justify-center text-center px-5 py-10 max-w-3xl">
          <h1 className="text-3xl font-semibold text-rs-off-white mb-4">Your ResinSmart Free Trial Has Ended</h1>
          <p className="text-rs-off-white/80 mb-4">Thanks for spending time in ResinSmart. We hope the platform helped bring clarity to where your resin pricing stands, what the market is signaling, and where leverage may exist.</p>
          <p className="text-rs-off-white/80 mb-4">If you’d like continue to access real-time pricing benchmarks for feedstocks and fair-market resin pricing or talk through which subscription tier makes sense for your team, please reach out directly:</p>
          <p className="text-rs-off-white mb-1">Michael Workman</p>
          <p className="text-rs-off-white mb-1">Growth Director, ResinSmart</p>
          <p className="text-rs-off-white mb-6">📧 michael.workman@resinsmart.ai</p>
          <p className="text-rs-off-white/80 mb-8">We’re happy to help you decide whether continuing the service aligns with your procurement strategy—no pressure either way.</p>
          <a
            href="https://calendly.com/mworkman-zhq7/resinsmart-discussion"
            target="_blank"
            rel="noreferrer"
            className="px-6 py-3 bg-rs-green text-rs-black hover:bg-rs-dark-green transition"
          >
            Restore ResinSmart Access
          </a>
        </div>
      ) : (
        <div className="h-full mt-5 w-full flex items-center max-w-8xl justify-center min-w-0">
          <div data-testid="dashboard-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[325px_1fr] lg:grid-rows-2 gap-6 dashboard w-full p-5 min-w-0 items-start justify-center ">
            <DashboardSidebar
              onNavigate={handleNavigate}
              hasEmployeeAccess={hasEmployeeAccess}
              activeReports={activeReports}
              useResinDashboards={useResinDashboards}
            />

            <DashboardGrid
              reportsGroupings={reportsGroupings}
              gradients={gradients}
              textColors={textColors}
              hasPremiumAccess={hasPremiumAccess}
              useResinDashboards={useResinDashboards}
              onNavigate={handleNavigate}
              loading={loading}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
